import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { Panel } from '@/components/Panel';

/**
 * Encadré repliable (région nommée par son titre) : le titre, son nombre d'éléments et sa description ouvrent ou
 * referment le contenu ; actions (filtres…) à droite, masquées une fois replié. defaultCollapsed : replié à l'arrivée.
 * lazy : contenu monté à la première ouverture seulement (ses requêtes avec), puis gardé.
 * Le repli n'est pas mémorisé (à rediscuter en fin de projet, chef de projet 2026-10-29).
 */
export function CollapsiblePanel({
  title,
  description,
  count,
  actions,
  defaultCollapsed = false,
  lazy = false,
  children,
  ...props
}: {
  title: string;
  description?: ReactNode;
  /** Nombre d'éléments, affiché à côté du titre (même replié) ; absent pendant le chargement */
  count?: number;
  actions?: ReactNode;
  /** Replié à l'arrivée */
  defaultCollapsed?: boolean;
  /** Contenu monté à la première ouverture (pas de chargement tant qu'il reste replié) */
  lazy?: boolean;
  children: ReactNode;
  'data-testid'?: string;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [opened, setOpened] = useState(!defaultCollapsed);
  const toggle = () => {
    setCollapsed(!collapsed);
    setOpened(true);
  };
  const contentId = useId();

  return (
    <Panel as="section" aria-label={title} className="overflow-hidden" {...props}>
      <div
        className={clsx(
          'px-6 py-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between',
          !collapsed && 'border-b border-slate-200',
        )}
      >
        <div>
          {/* Motif accordéon : le bouton dans le titre, qui reste un titre pour les lecteurs d'écran */}
          <h3 className="text-base font-bold text-slate-900">
            <button
              type="button"
              className="flex items-center gap-2 text-left font-bold group cursor-pointer"
              aria-expanded={!collapsed}
              aria-controls={contentId}
              onClick={toggle}
            >
              <ChevronDown
                className={clsx(
                  'w-5 h-5 shrink-0 text-slate-400 group-hover:text-slate-600 transition-transform',
                  collapsed && '-rotate-90',
                )}
              />
              {title}
              {count !== undefined && (
                <span
                  className="px-2 py-0.5 rounded-full bg-slate-100 text-xs font-semibold text-slate-600"
                  data-testid="collapsible-count"
                >
                  {count}
                </span>
              )}
            </button>
          </h3>
          {description && <p className="text-sm text-slate-500 mt-1 ml-7">{description}</p>}
        </div>
        {!collapsed && actions}
      </div>
      <div id={contentId} hidden={collapsed}>
        {(!lazy || opened) && children}
      </div>
    </Panel>
  );
}
